import { CalendarRange, Pencil, Plus, Send, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import type { TimetableVersion } from '@/types/timetableAdmin';
import { describeTimetableRange } from '../timetableRange';

export interface TimetableRangeBarProps {
  /** The section's timetables, earliest first. */
  timetables: TimetableVersion[];
  /** The one the week on screen is built from, or null when none covers it. */
  shown: TimetableVersion | null;
  canManage: boolean;
  /** Picking a timetable moves the screen to its first week. */
  onPick: (timetable: TimetableVersion) => void;
  onCreate: () => void;
  onEditDates: (timetable: TimetableVersion) => void;
  onRemove: (timetable: TimetableVersion) => void;
  /** Gives a draft to teachers and families. Offered only on a draft. */
  onPublish: (timetable: TimetableVersion) => void;
}

const BUTTON =
  '!h-9 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] text-slate-700 dark:text-slate-200 px-3 text-[13px] font-bold transition-all shadow-2xs cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 shrink-0';

/**
 * Which of the section's timetables the grid below is showing, and the dates
 * it applies between.
 *
 * A section can change timetable mid-session, so the week on screen is always
 * read from the timetable in force that week — this bar says which one, so a
 * lesson placed in the grid is never placed in a timetable the office did not
 * mean. Paging through weeks moves between timetables on its own; the picker
 * is the shortcut to another one's first week.
 */
export function TimetableRangeBar({
  timetables,
  shown,
  canManage,
  onPick,
  onCreate,
  onEditDates,
  onRemove,
  onPublish,
}: TimetableRangeBarProps) {
  const isDraft = shown?.publication === 'DRAFT';

  return (
    <section
      aria-label="Timetable dates"
      className="rounded-2xl p-3 bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between print:hidden"
    >
      <div className="flex items-start gap-3 min-w-0">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-indigo-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400">
          <CalendarRange className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-[12px] font-bold text-slate-500 dark:text-slate-400">
            Timetable for this week
          </p>
          <div className="flex min-w-0 items-center gap-2" role="status">
            <p className="text-sm font-black text-slate-800 dark:text-white truncate">
              {shown
                ? `${describeTimetableRange(shown)} · ${shown.lessons} ${shown.lessons === 1 ? 'Period' : 'Periods'}`
                : 'None — no timetable applies to this week'}
            </p>
            {isDraft && (
              <Badge tone="warning" className="shrink-0">
                Draft — only the office can see it
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        {timetables.length > 1 && (
          <div className="w-full sm:w-64">
            <Select
              label="Go to timetable"
              labelClassName="text-[12px] font-bold text-slate-700 dark:text-slate-200"
              options={timetables.map((timetable) => ({
                value: timetable.uuid,
                label:
                  timetable.publication === 'DRAFT'
                    ? `${describeTimetableRange(timetable)} · Draft`
                    : describeTimetableRange(timetable),
              }))}
              value={shown?.uuid ?? ''}
              placeholder="Choose a timetable"
              onChange={(event) => {
                const picked = timetables.find(
                  (timetable) => timetable.uuid === event.target.value,
                );
                if (picked) onPick(picked);
              }}
              className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            />
          </div>
        )}

        {canManage && shown && (
          <>
            {isDraft && (
              <button
                type="button"
                onClick={() => onPublish(shown)}
                className="!h-9 inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 text-[13px] font-bold transition-all shadow-sm cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 shrink-0"
              >
                <Send className="h-3.5 w-3.5" aria-hidden />
                <span>Publish</span>
              </button>
            )}
            <button type="button" className={BUTTON} onClick={() => onEditDates(shown)}>
              <Pencil className="h-3.5 w-3.5 text-slate-500" aria-hidden />
              <span>Change dates</span>
            </button>
            <button
              type="button"
              className={`${BUTTON} text-rose-600 dark:text-rose-400 hover:text-rose-700 hover:border-rose-300`}
              onClick={() => onRemove(shown)}
            >
              <Trash2 className="h-3.5 w-3.5 text-rose-500" aria-hidden />
              <span>Delete</span>
            </button>
          </>
        )}

        {canManage && (
          <button
            type="button"
            onClick={onCreate}
            className="!h-9 inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 text-[13px] font-bold transition-all shadow-sm cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 shrink-0"
          >
            <Plus className="h-4 w-4" aria-hidden />
            <span>Create timetable</span>
          </button>
        )}
      </div>
    </section>
  );
}
